import React from 'react';
import type { ColumnDef, StockFeatures } from '@tanstack/react-table';
import { useNavigate, useSearch } from '@tanstack/react-router';
import {
  Banner,
  Button,
  DataTable,
  EmptyState,
  EmptyStateSpeedGraphic,
  FilterToolbar,
  LoadingOrbit,
  Page,
  PageHeader,
  TagBadge,
  Text,
  ToolbarFilterMenu,
  type MultiSelectOption,
} from '@chatsdet/ui';
import { extractUuid } from '@chatsdet/ids';
import { Gauge } from '@phosphor-icons/react';
import { usePropertySummaries } from '../../../hooks/usePropertyMetadata';
import { formatRelativeTimeAgo } from '../../../features/workflows/useAgenticRunDetail';
import {
  PERF_STATUSES,
  PERF_VERDICTS,
  parsePerfStatus,
  parsePerfVerdict,
  type PerfSearch,
} from '../../../features/perf/perfSearch';
import { BRAND } from '../../../brand';
import { FeatureOnboardingEmptyState } from '../../components/FeatureOnboardingEmptyState';
import { shortSha } from '@chatsdet/types/perf';
import {
  formatHeadlineDelta,
  largestGatedDelta,
  runVerdictPresentation,
  statusPresentation,
  verdictPresentation,
} from '../../../features/perf/perfPresentation';
import {
  useHasPerfRuns,
  useLatestPerfRuns,
  usePerfRuns,
  usePerfScheduledTestUuids,
  usePerfTests,
  type PerfRunListItem,
  type PerfTestListItem,
} from '../../../features/perf/usePerf';

type TestRow = PerfTestListItem & {
  applicationName: string;
  lastRun: PerfRunListItem | null;
  scheduled: boolean;
};

const TEST_COLUMNS: ColumnDef<StockFeatures, TestRow>[] = [
  {
    id: 'name',
    header: 'Name',
    enableSorting: false,
    cell: ({ row }) => <Text size="sm" className="font-medium truncate">{row.original.name}</Text>,
    meta: { flex: 3, minWidth: 200 },
  },
  {
    id: 'application',
    header: 'Application',
    enableSorting: false,
    cell: ({ row }) => <Text size="sm">{row.original.applicationName}</Text>,
    meta: { flex: 2, minWidth: 140 },
  },
  {
    id: 'environment',
    header: 'Environment',
    enableSorting: false,
    cell: ({ row }) => <Text size="sm">{row.original.environment}</Text>,
    meta: { flex: 1, minWidth: 120 },
  },
  {
    id: 'verdict',
    header: 'Last verdict',
    enableSorting: false,
    cell: ({ row }) => {
      const run = row.original.lastRun;
      if (!run) return <Text size="sm" className="text-muted-foreground">No runs</Text>;
      const { label, color } = runVerdictPresentation(run);
      return <TagBadge name={label} color={color} size="md" />;
    },
    meta: { minWidth: 130 },
  },
  {
    id: 'lastRun',
    header: 'Last run',
    enableSorting: false,
    cell: ({ row }) => (
      <Text size="sm" className="text-muted-foreground">
        {row.original.lastRun ? formatRelativeTimeAgo(row.original.lastRun.createdAt) : '—'}
      </Text>
    ),
    meta: { minWidth: 110 },
  },
  {
    id: 'schedule',
    header: 'Schedule',
    enableSorting: false,
    cell: ({ row }) =>
      row.original.scheduled ? (
        <TagBadge name="Scheduled" color="blue" size="md" />
      ) : (
        <Text size="sm" className="text-muted-foreground">Manual</Text>
      ),
    meta: { minWidth: 110 },
  },
];

const RUN_COLUMNS: ColumnDef<StockFeatures, PerfRunListItem>[] = [
  {
    id: 'head',
    header: 'Head',
    enableSorting: false,
    cell: ({ row }) => <Text size="sm" className="font-mono">{shortSha(row.original.headSha)}</Text>,
    meta: { minWidth: 100 },
  },
  {
    id: 'base',
    header: 'Base',
    enableSorting: false,
    cell: ({ row }) => <Text size="sm" className="font-mono">{shortSha(row.original.baseSha)}</Text>,
    meta: { minWidth: 100 },
  },
  {
    id: 'status',
    header: 'Status',
    enableSorting: false,
    cell: ({ row }) => {
      const { label, color } = statusPresentation(row.original.status);
      return <TagBadge name={label} color={color} size="md" />;
    },
    meta: { minWidth: 120 },
  },
  {
    id: 'verdict',
    header: 'Verdict',
    enableSorting: false,
    cell: ({ row }) => {
      const { verdict } = row.original;
      if (!verdict) return <Text size="sm" className="text-muted-foreground">—</Text>;
      const { label, color } = verdictPresentation(verdict);
      return <TagBadge name={label} color={color} size="md" />;
    },
    meta: { minWidth: 130 },
  },
  {
    id: 'delta',
    header: 'Largest gated delta',
    enableSorting: false,
    cell: ({ row }) => {
      const delta = largestGatedDelta(row.original.headlineDeltas);
      return delta ? (
        <Text size="sm" className="tabular-nums">{`${delta.label} ${formatHeadlineDelta(delta)}`}</Text>
      ) : (
        <Text size="sm" className="text-muted-foreground">—</Text>
      );
    },
    meta: { flex: 2, minWidth: 200 },
  },
  {
    id: 'started',
    header: 'Started',
    enableSorting: false,
    cell: ({ row }) => (
      <Text size="sm" className="text-muted-foreground">{formatRelativeTimeAgo(row.original.createdAt)}</Text>
    ),
    meta: { minWidth: 110 },
  },
];

const ONBOARDING_PROMPT =
  `Use the ${BRAND.name} CLI to set up performance tests for this app. First run \`${BRAND.cliCommand} perf guide\` and follow it. Then save a test for the most important page or journey and run it once to record a first measurement.`;
const ONBOARDING_STEPS = [
  'Save a performance test for a page or journey you care about.',
  'Each run measures your branch (head) and the main branch (base) side by side.',
  `${BRAND.name} compares the two and gives a verdict: faster, slower, or no real difference.`,
  'Saved tests and recent runs are listed here.',
];

const STATUS_OPTIONS: MultiSelectOption[] = PERF_STATUSES.map((id) => ({
  id,
  label: statusPresentation(id).label,
}));
const VERDICT_OPTIONS: MultiSelectOption[] = PERF_VERDICTS.map((id) => ({
  id,
  label: verdictPresentation(id).label,
}));

export function PerfPage() {
  const search = useSearch({ from: '/performance' });
  const navigate = useNavigate();
  const tests = usePerfTests();
  const properties = usePropertySummaries();
  const schedules = usePerfScheduledTestUuids();
  const runs = usePerfRuns({ adHoc: true, status: search.status, verdict: search.verdict });
  const testList = tests.data ?? [];
  const hasRuns = useHasPerfRuns(tests.isSuccess && testList.length === 0);
  const latest = useLatestPerfRuns(testList.map((t) => t.id));

  const testRows: TestRow[] = testList.map((test) => ({
    ...test,
    applicationName: properties.data?.find((p) => extractUuid(p.id) === extractUuid(test.applicationId))?.name ?? '—',
    lastRun: latest.get(test.id) ?? null,
    scheduled: schedules.data?.has(extractUuid(test.id) ?? '') ?? false,
  }));

  const adHocRuns = runs.data?.pages.flatMap((p) => p.items) ?? [];

  const neverUsed =
    !tests.isLoading && !tests.isError && testList.length === 0 && hasRuns.isFetchedAfterMount && hasRuns.data === false;
  // Tests are empty and the "any runs?" answer has not landed since mount (focus refetches keep the cached answer).
  const settling = tests.isSuccess && testList.length === 0 && !hasRuns.isFetchedAfterMount;
  const hasRunFilters = Boolean(search.status || search.verdict);

  const updateSearch = (updates: Partial<PerfSearch>) =>
    navigate({ to: '/performance', search: (prev: PerfSearch) => ({ ...prev, ...updates }) });

  return (
    <Page>
      <PageHeader
        icon={<Gauge size={24} weight="fill" />}
        title="Performance"
        description="Saved performance tests and A/B runs comparing a head version against its base."
      />
      {neverUsed ? (
        <FeatureOnboardingEmptyState
          icon={<Gauge size={48} />}
          graphic={<EmptyStateSpeedGraphic />}
          title="Catch slowdowns before they ship"
          whatItIs="Performance tests measure how fast a page or journey loads, and compare your change against the main branch so you can see if it got slower."
          steps={ONBOARDING_STEPS}
          agentPrompt={ONBOARDING_PROMPT}
        />
      ) : tests.isError ? (
        <Banner variant="error">{tests.error.message}</Banner>
      ) : testList.length === 0 && hasRuns.isError ? (
        <Banner variant="error">{hasRuns.error.message}</Banner>
      ) : tests.isLoading || settling ? (
        <div className="flex items-center justify-center py-12">
          <LoadingOrbit size="lg" />
        </div>
      ) : testRows.length === 0 ? (
        <EmptyState
          icon={<Gauge size={48} />}
          title="No saved performance tests"
          description={`Runs exist, but none are saved as a test yet. Save one with the ${BRAND.name} CLI (perf save) and it will show up here.`}
        />
      ) : (
        <DataTable
          columns={TEST_COLUMNS}
          data={testRows}
          aria-label="Performance tests table"
          getRowId={(row) => row.id}
          getRowHref={(row) => `/performance/tests/${row.id}`}
        />
      )}

      {!neverUsed && !settling && (
        <>
      <PageHeader size="section" title="Recent runs not tied to a test (ad hoc)" />
      <FilterToolbar>
        <ToolbarFilterMenu
          label="Status"
          options={STATUS_OPTIONS}
          selectedIds={search.status ? [search.status] : []}
          selectionMode="single"
          searchable={false}
          onChange={(ids) => updateSearch({ status: parsePerfStatus(ids[0]) })}
        />
        <ToolbarFilterMenu
          label="Verdict"
          options={VERDICT_OPTIONS}
          selectedIds={search.verdict ? [search.verdict] : []}
          selectionMode="single"
          searchable={false}
          onChange={(ids) => updateSearch({ verdict: parsePerfVerdict(ids[0]) })}
        />
      </FilterToolbar>
      {runs.isError ? (
        <Banner variant="error">{runs.error.message}</Banner>
      ) : runs.isLoading ? (
        <div className="flex items-center justify-center py-12">
          <LoadingOrbit size="lg" />
        </div>
      ) : adHocRuns.length === 0 ? (
        hasRunFilters ? (
          <EmptyState
            icon={<Gauge size={48} />}
            title="No results for these filters"
            description="No ad hoc runs match the current filters."
            action={
              <Button
                variant="outline"
                size="sm"
                onPress={() => updateSearch({ status: undefined, verdict: undefined })}
              >
                Clear filters
              </Button>
            }
          />
        ) : (
          <EmptyState
            icon={<Gauge size={48} />}
            title="No ad hoc runs"
            description="Runs started with perf run and not saved to a test appear here."
          />
        )
      ) : (
        <DataTable
          columns={RUN_COLUMNS}
          data={adHocRuns}
          aria-label="Ad hoc performance runs table"
          getRowId={(row) => row.id}
          getRowHref={(row) => `/performance/runs/${row.id}`}
        />
      )}
      {runs.hasNextPage && (
        <div className="flex justify-center py-4">
          <Button variant="outline" onPress={() => runs.fetchNextPage()} isDisabled={runs.isFetchingNextPage}>
            {runs.isFetchingNextPage ? 'Loading...' : 'Load more'}
          </Button>
        </div>
      )}
        </>
      )}
    </Page>
  );
}
